import { GraphQLErrors } from "@apollo/client/errors";
import { serialize } from "cookie";
import { GraphQLFormattedError } from "graphql";
import {
    MixUserByRefreshTokenDocument,
    UpdateMeDocument
} from "graphql/generated";
import jwt from "jsonwebtoken";
import { getClient } from "utils/client";
import {
    RefreshTokenExpiredError,
    RefreshTokenLostError,
    RefreshTokenNotExistsError
} from "utils/errors";

export const generateAccessToken = (userId: string) => {
    return jwt.sign(
        {
            sub: "user-access-token",
            "https://hasura.io/jwt/claims": {
                "x-hasura-allowed-roles": ["guest", "user"],
                "x-hasura-default-role": "user",
                "x-hasura-user-id": userId
            }
        } as UserJWTPayload,
        process.env.HASURA_GRAPHQL_JWT_SECRET,
        {
            expiresIn: "15m" // Same for both dev and prod
        }
    );
};

/**
 * @param refreshToken
 * @throws {RefreshTokenNotExistsError} The refresh token is empty.
 * @throws {RefreshTokenLostError} A user who has refresh token is not exists.
 * @throws {RefreshTokenExpiredError} The refresh token is expired.
 * @returns
 */
export const fetchAccessToken = async (refreshToken?: string | string[]) => {
    if (typeof refreshToken !== "string") {
        throw new RefreshTokenNotExistsError("Refresh token is not exists!");
    }

    const client = getClient({
        headers: {
            "x-hasura-admin-secret": process.env.HASURA_ADMIN_SECRET
        },
        ctx: null
    });

    const { data } = await client.query<
        MixUserByRefreshTokenQuery,
        MixUserByRefreshTokenQueryVariables
    >({
        query: MixUserByRefreshTokenDocument,
        variables: {
            refreshToken
        }
    });

    const user = data.mix_user[0];

    if (!user) throw new RefreshTokenLostError("user is not exists!");

    if (
        !user.refreshTokenExpiry ||
        new Date(user.refreshTokenExpiry + "Z") < new Date()
    ) {
        client.mutate<UpdateMeMutation, UpdateMeMutationVariables>({
            mutation: UpdateMeDocument,
            variables: {
                id: user.id,
                _set: {
                    refreshToken: null,
                    refreshTokenExpiry: null
                }
            }
        });

        throw new RefreshTokenExpiredError("refresh token is expired!");
    }

    return generateAccessToken(user.id);
};

interface CookieOptions {
    httpOnly: boolean;
    secure: boolean;
    sameSite: "lax" | "strict" | "none";
    domain?: string;
    path: string;
    expires?: Date;
}

const getDefaultCookieOptions = (): CookieOptions => ({
    httpOnly: true,
    secure: process.env.NODE_ENV === "production",
    sameSite: "lax",
    domain: process.env.NODE_ENV === "production" ? "mix.day" : undefined,
    path: "/",
    expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 3) // 3 days
});

export const buildAccessTokenCookie = (accessToken: string) => {
    // accessToken: httpOnly (JS 접근 불가)
    // accessTokenExp: non-httpOnly — 클라이언트 Apollo link가 만료 여부 확인용으로만 사용
    const decoded = jwt.decode(accessToken) as { exp?: number } | null;
    const expMs = decoded?.exp ? decoded.exp * 1000 : Date.now() + 1000 * 60 * 15;
    const expCookieOptions = {
        ...getDefaultCookieOptions(),
        httpOnly: false,
        expires: new Date(expMs)
    };
    return [
        serialize("accessToken", accessToken, getDefaultCookieOptions()),
        serialize("accessTokenExp", String(Math.floor(expMs / 1000)), expCookieOptions)
    ];
};

export const buildRefreshTokenCookie = (
    refreshToken: string,
    refreshTokenExpiry: Date
) => {
    return [
        serialize("refreshToken", refreshToken, {
            ...getDefaultCookieOptions(),
            expires: refreshTokenExpiry
        })
    ];
};

export const buildSignOutCookie = () => {
    return [
        serialize("accessToken", "deleted", {
            ...getDefaultCookieOptions(),
            expires: new Date(0)
        }),
        serialize("accessTokenExp", "deleted", {
            ...getDefaultCookieOptions(),
            httpOnly: false,
            expires: new Date(0)
        }),
        serialize("refreshToken", "deleted", {
            ...getDefaultCookieOptions(),
            expires: new Date(0)
        })
    ];
};

export const isHasuraJWTExpired = (
    error: GraphQLErrors | readonly GraphQLFormattedError[]
) => {
    return !!error.find((e) => e?.extensions?.code === "invalid-jwt");
};

// JWT의 exp 클레임을 직접 디코딩하여 만료 여부를 확인
// Hasura의 guest role fallback보다 먼저 서버에서 감지하기 위해 사용
export const isAccessTokenExpired = (accessToken: string): boolean => {
    try {
        const decoded = jwt.decode(accessToken) as { exp?: number } | null;
        if (!decoded?.exp) return true;
        return decoded.exp * 1000 < Date.now();
    } catch {
        return true;
    }
};

// mix-admin(admin.mix.day)도 mix.day 도메인에 같은 이름의 accessToken 쿠키로 reviewer 토큰을 저장함
// Hasura는 이 토큰을 유효하다고 보고 reviewer role로 응답하므로, user role이 아니면 무효로 간주
export const isUserAccessToken = (accessToken: string): boolean => {
    if (isAccessTokenExpired(accessToken)) return false;
    const decoded = jwt.decode(accessToken) as UserJWTPayload | null;
    return (
        decoded?.["https://hasura.io/jwt/claims"]?.["x-hasura-default-role"] ===
        "user"
    );
};
